/* eslint-disable */
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Bot, Sparkles, ArrowLeft, Scale } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChat } from "../context/ChatContext";

const SUGGESTED_QUESTIONS = [
  "Bagaimana proses gugatan cerai di Indonesia?",
  "Apa syarat mendirikan PT (Perseroan Terbatas)?",
  "Bagaimana cara mengurus sertifikat tanah?",
  "Apa hak-hak saya jika di-PHK?",
];

export default function ChatBotPage() {
  const navigate = useNavigate();
  const { messages, isTyping, sendMessage } = useChat();

  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = (e, overrideText) => {
    if (e) e.preventDefault();
    const textToSend = overrideText ?? input;
    if (!textToSend.trim()) return;
    sendMessage(textToSend);
    setInput("");
  };

  const handleSuggest = (question) => {
    handleSend(null, question);
  };

  const isFirstMessage = messages.length === 1;

  return (
    <div className="min-h-screen bg-navy-950 flex flex-col relative overflow-hidden">
      {/* Ambient background orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-gold-500/5 blur-[120px]" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-navy-600/20 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gold-400/3 blur-[160px]" />
      </div>

      {/* ── Header ── */}
      <header className="relative z-20 border-b border-white/5 bg-navy-950/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Back button */}
          <motion.button
            onClick={() => navigate(-1)}
            whileHover={{ x: -3 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 text-navy-300 hover:text-white transition-colors text-sm font-medium group"
          >
            <ArrowLeft
              size={18}
              className="group-hover:text-gold-400 transition-colors"
            />
            <span>Kembali</span>
          </motion.button>

          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-gold-300 to-gold-600 p-[1.5px]">
              <div className="w-full h-full bg-navy-950 rounded-xl flex items-center justify-center">
                <Bot size={18} className="text-gold-400" />
              </div>
            </div>
            <div className="hidden sm:block">
              <p className="text-white text-sm font-bold tracking-wide leading-none">
                Legal AI Assistant
              </p>
              <p className="text-gold-400/70 text-[11px] mt-0.5 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block animate-pulse" />
                Online dan Siap Membantu!
              </p>
            </div>
          </div>

        </div>
      </header>

      {/* ── Messages ── */}
      <main className="relative z-10 flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex flex-col gap-6 min-h-full">
          {/* Welcome hero shown when only the initial message exists */}
          {isFirstMessage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center py-8"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-gold-300 to-gold-600 p-[2px] mb-5 shadow-[0_0_40px_rgba(212,149,14,0.3)]">
                <div className="w-full h-full bg-navy-900 rounded-3xl flex items-center justify-center">
                  <Scale size={36} className="text-gold-400" />
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Konsultasi Hukum AI
              </h1>
              <p className="text-navy-300 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
                Ajukan pertanyaan hukum Anda dan dapatkan panduan awal dari
                asisten AI kami, tersedia 24 jam sehari.
              </p>
            </motion.div>
          )}

          {/* Messages list */}
          {messages.map((msg, i) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i === 0 ? 0.2 : 0 }}
              className={`flex gap-3 ${msg.isBot ? "justify-start" : "justify-end"}`}
            >
              {/* Bot avatar */}
              {msg.isBot && (
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-gold-300 to-gold-600 p-[1.5px]">
                    <div className="w-full h-full bg-navy-900 rounded-xl flex items-center justify-center">
                      <Bot size={14} className="text-gold-400" />
                    </div>
                  </div>
                </div>
              )}

              <div
                className={`flex flex-col max-w-[75%] sm:max-w-[65%] ${msg.isBot ? "items-start" : "items-end"}`}
              >
                <div
                  className={`px-5 py-3.5 text-sm sm:text-base leading-relaxed rounded-2xl shadow-lg ${
                    msg.isBot
                      ? "bg-navy-900/80 text-navy-50 rounded-tl-sm border border-white/5 backdrop-blur-sm"
                      : "bg-gradient-to-br from-gold-400 to-gold-600 text-navy-950 rounded-tr-sm font-medium"
                  }`}
                >
                  {msg.text}
                </div>

                {/* Redirect to WhatsApp button */}
                {msg.isRedirect && msg.waUrl && (
                  <a
                    href={msg.waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 active:scale-95 text-white text-sm font-semibold py-2.5 px-5 rounded-xl shadow-md transition-all"
                  >
                    <Sparkles size={14} />
                    Lanjut ke WhatsApp
                  </a>
                )}

                <span
                  className={`text-[10px] text-navy-500 mt-1.5 px-1 ${msg.isBot ? "text-left" : "text-right"}`}
                >
                  {msg.isBot ? "AI Assistant" : "Anda"} &bull;{" "}
                  {new Date(msg.id).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </motion.div>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex gap-3 justify-start"
            >
              <div className="flex-shrink-0 mt-1 w-8 h-8 rounded-xl bg-gradient-to-br from-gold-300 to-gold-600 p-[1.5px]">
                <div className="w-full h-full bg-navy-900 rounded-xl flex items-center justify-center">
                  <Bot size={14} className="text-gold-400" />
                </div>
              </div>
              <div className="px-5 py-4 bg-navy-900/80 rounded-2xl rounded-tl-sm border border-white/5 backdrop-blur-sm shadow-lg flex gap-1.5 items-center">
                {[0, 0.2, 0.4].map((delay, i) => (
                  <motion.span
                    key={i}
                    animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
                    transition={{ repeat: Infinity, duration: 1.2, delay }}
                    className="w-1.5 h-1.5 bg-gold-400 rounded-full"
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* Suggested questions (shown only when chat is fresh) */}
          <AnimatePresence>
            {isFirstMessage && !isTyping && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ delay: 0.4 }}
                className="mt-2"
              >
                <p className="text-navy-400 text-xs font-medium mb-3 flex items-center gap-2">
                  <Sparkles size={11} className="text-gold-500" />
                  Pertanyaan yang sering diajukan
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SUGGESTED_QUESTIONS.map((q, i) => (
                    <motion.button
                      key={i}
                      onClick={() => handleSuggest(q)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="text-left px-4 py-3 rounded-xl bg-navy-900/60 border border-white/5 hover:border-gold-500/30 hover:bg-navy-900/90 text-navy-200 text-sm transition-all leading-snug"
                    >
                      {q}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div ref={messagesEndRef} className="h-4" />
        </div>
      </main>

      {/* ── Input Bar ── */}
      <div className="relative z-20 border-t border-white/5 bg-navy-950/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <form
            onSubmit={handleSend}
            className="relative flex items-center gap-3"
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ketik pertanyaan hukum Anda..."
              className="flex-1 bg-navy-900/60 text-white text-sm rounded-2xl pl-5 pr-5 py-4 outline-none border border-white/10 focus:border-gold-500/50 focus:bg-navy-900/90 transition-all placeholder-navy-400 shadow-inner backdrop-blur-md"
            />
            <motion.button
              type="submit"
              aria-label="Kirim Pesan"
              disabled={!input.trim() || isTyping}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center text-navy-950 disabled:opacity-40 disabled:grayscale transition-all shadow-[0_4px_20px_rgba(212,149,14,0.4)]"
            >
              <Send size={18} className="ml-0.5" />
            </motion.button>
          </form>
          <p className="text-center mt-3 text-[10px] text-navy-500 flex items-center justify-center gap-1">
            <Sparkles size={9} className="text-gold-500" />
            AI dapat melakukan kesalahan. Harap verifikasi informasi hukum
            dengan profesional.
          </p>
        </div>
      </div>
    </div>
  );
}
